import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  loading: boolean;
  contact: FunnelContact | null;
  newJudgement: string;

  productGroup: ProductGroups;

  handleNewJudgementChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  handleChangeAiJudgement(): void;
}

const ChangeAiJudgementDialog: React.FC<Props> = ({
  open,
  loading,
  contact,
  newJudgement,
  productGroup,
  handleNewJudgementChange,
  handleClose,
  handleChangeAiJudgement,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const contactName = contact?.fullName || `${contact?.firstName || ''} ${contact?.lastName || ''}` || t('linkedinUser');

  const getLocalizedText = (key: string): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey) || t(key);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey) || t(key);
  };

  const options = [
    { label: getLocalizedText('topCandidates'), value: 'A' },
    { label: getLocalizedText('strongContenders'), value: 'B' },
    { label: getLocalizedText('backupCandidatePool'), value: 'C' },
  ];

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('changeAiJudgement')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <Typography className={classes.contactName}>
            <Trans i18nKey="changeAiJudgementFor" values={{ contactName }}>
              Change AI judgement for <strong>{contactName}</strong>
            </Trans>
          </Typography>
          <PaiSelect value={newJudgement} variant={'background'} onChange={handleNewJudgementChange}>
            {options.map((option) => (
              <PaiMenuItem key={option.value} value={option.value}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('save')} size={'small'} border={'rounded'} loading={loading} action={handleChangeAiJudgement} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  contactName: {
    fontSize: theme.typography.pxToRem(15),
  },
  content: {
    padding: '24px !important',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default ChangeAiJudgementDialog;
